import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import AuthSection from '../AuthSection';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../Button';
import LoadingSpinner from '../LoadingSpinner';
import { FormInput } from '../FormInput/FormInput';
import { Form } from '../Form/Form';

import styles from './SignUp.module.scss';

export function SignUp(): React.JSX.Element {
  const { signup } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const schema = yup.object().shape({
    email: yup.string().email('Некорректный email').required('Email обязателен'),
    password: yup
      .string()
      .required('Пароль обязателен')
      .min(8, 'Должно быть минимум 8 символов')
      .matches(/[A-Z]/i, 'Должна быть хотя бы одна буква')
      .matches(/\d/, 'Должна быть хотя бы одна цифра')
      .matches(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, 'Должнен быть хотя бы один спецсимвол'),
    confirmPassword: yup
      .string()
      .required('Подтверждение пароля обязательно')
      .oneOf([yup.ref('password')], 'Пароли не совпадают'),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    try {
      await schema.validate({ email, password, confirmPassword }, { abortEarly: false });

      setLoading(true);
      await signup(email, password, confirmPassword);
    } catch (err: unknown) {
      if (err instanceof yup.ValidationError) {
        const fieldErrors: Record<string, string> = {};
        err.inner.forEach((e: yup.ValidationError) => {
          if (e.path && !fieldErrors[e.path]) {
            fieldErrors[e.path] = e.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        setGlobalError('Ошибка при регистрации. Попробуйте снова.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSection>
      <div className={styles.signup}>
        <div className={styles.title}>
          <h1>Создать аккаунт</h1>
          <p>Присоединяйтесь к нам!</p>
        </div>

        <Form onSubmit={handleSubmit} className={styles.signupForm}>
          <FormInput
            label="Email"
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            disabled={loading}
            error={errors.email}
          />
          <FormInput
            label="Пароль"
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            disabled={loading}
            error={errors.password}
          />
          <FormInput
            label="Подтвердите пароль"
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            error={errors.confirmPassword}
          />

          {globalError && <div className={styles.globalError}>{globalError}</div>}

          <Button className={styles.signupFormButton} type="submit" variant="default" disabled={loading}>
            {loading ? <LoadingSpinner size="sm" /> : 'Зарегистрироваться'}
          </Button>

          <div className={styles.login}>
            Уже есть аккаунт? <Link to="/login">Войти</Link>
          </div>
        </Form>
      </div>
    </AuthSection>
  );
}
